import { type FC, useMemo } from 'react';
import type { RefObject } from 'react';

import Spreadsheet from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';

import type { SampleType, SampleTypeRow } from '@/model/SampleType.model';
import type { ValidationData } from '@/utils/sampleTypeValidation';

import {
  buildColumns,
  initBulkEditSampleTypesSettings,
  buildCellsCallback,
  buildAfterRendererCallback,
} from './BulkEditSampleTypes.utils';

interface HotTableRefLike {
  hotInstance?: unknown;
}

interface BulkEditSampleTypesTableProps {
  data: Array<SampleTypeRow>;
  innerRef: RefObject<HotTableRefLike | null>;
  sampleTypes: Array<SampleType>;
  validationData: ValidationData | null;
  availableDetailOptions: Array<string>;
  setInvalid: (invalid: boolean) => void;
}

const BulkEditSampleTypesTable: FC<BulkEditSampleTypesTableProps> = ({
  data,
  innerRef,
  sampleTypes,
  validationData,
  availableDetailOptions,
  setInvalid,
}) => {
  const columns = useMemo(
    () =>
      buildColumns({
        availableDetailOptions,
        validationData,
        sampleTypes,
        hotTableRef: innerRef,
      }),
    [availableDetailOptions, validationData, sampleTypes, innerRef]
  );

  const settings = useMemo<SpreadsheetSettings>(() => initBulkEditSampleTypesSettings(columns), [columns]);

  const cellsCallback = useMemo(
    () => buildCellsCallback({ sampleTypes, validationData }),
    [sampleTypes, validationData]
  );

  const afterRendererCallback = useMemo(() => buildAfterRendererCallback(sampleTypes), [sampleTypes]);

  return (
    <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles">
      <Spreadsheet
        className="mt3"
        testPrefix="bulk-edit-sample-types"
        data={data as unknown as Array<Record<string, unknown>>}
        innerRef={innerRef as RefObject<HotTableRef>}
        settings={settings}
        addCol={false}
        modalView={true}
        fillHandle={false}
        cells={cellsCallback}
        customAfterRenderer={afterRendererCallback}
        maxRows={data.length}
        setInvalid={setInvalid}
      />
    </div>
  );
};

export default BulkEditSampleTypesTable;
